'use es6';

import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import styled from 'styled-components';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { EntityChildren, EntityTypes, AdWordsKeywordServingStatusMap, Networks } from 'AdsUI/lib/Constants';
import { CANDY_APPLE } from 'HubStyleTokens/colors';
import UILink from 'UIComponents/link/UILink';
import CampaignReportingControlButtonsContainer from 'AdsUI/containers/campaign-details/CampaignReportingControlButtonsContainer';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import NetworkIcon from '../NetworkIcon';
import { AdStatusTag, AdGroupStatusTag, CampaignStatusTag } from 'AdsUI/components/campaigns/EntityStatusTag';
import UITruncateString from 'UIComponents/text/UITruncateString';
import TrackingStatusTag from './TrackingStatusTag';
import FBAdSetSummary from './FBAdSetSummary';
import FBAdSummary from './FBAdSummary';
import UIImage from 'UIComponents/image/UIImage';
import UITableHoverCell from 'UIComponents/table/UITableHoverCell';
import LIAdSetSummary from './LIAdSetSummary';
import LIAdSummary from './LIAdSummary';
import AdWordsReportingNameCell from 'AdsUI/components/campaign-reporting/AdWordsReportingNameCell';
import { KBArticles } from 'AdsUI/lib/Constants';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import UIButton from 'UIComponents/button/UIButton';
import UIBadge from 'UIComponents/badge/UIBadge';
import H4 from 'UIComponents/elements/headings/H4';
import KnowledgeBaseLink from 'AdsUI/components/KnowledgeBaseLink';
import withQueryParams from 'AdsUI/lib/withQueryParams';
import PortalIdParser from 'PortalIdParser';
const SingleLine = styled.div.withConfig({
  displayName: "CampaignReportingNameCell__SingleLine",
  componentId: "sc-1wemkoe-0"
})(["display:flex;overflow:hidden;width:100%;"]);

const CampaignStatusTagWrapper = ({
  entity
}) => /*#__PURE__*/_jsx(CampaignStatusTag, {
  campaignEntity: entity
});

const AdGroupStatusTagWrapper = ({
  entity,
  campaignEntity
}) => /*#__PURE__*/_jsx(AdGroupStatusTag, {
  adGroupEntity: entity,
  campaignEntity: campaignEntity
});

const AdStatusTagWrapper = ({
  entity,
  campaignEntity,
  parentEntity
}) => /*#__PURE__*/_jsx(AdStatusTag, {
  adEntity: entity,
  adGroupEntity: parentEntity,
  campaignEntity: campaignEntity
});

const NameInfo = ({
  entity
}) => {
  return /*#__PURE__*/_jsx(UITruncateString, {
    children: entity.name
  });
};

const AdWordsAdNameInfo = ({
  entity
}) => {
  return /*#__PURE__*/_jsx(AdWordsReportingNameCell, {
    entity: entity
  });
};

const AdWordsKeywordNameInfo = ({
  entity
}) => {
  const isRarelyServed = entity.servingStatus === AdWordsKeywordServingStatusMap.RARELY_SERVED;
  return /*#__PURE__*/_jsx(UITruncateString, {
    useFlex: isRarelyServed,
    fixedChildren: isRarelyServed && /*#__PURE__*/_jsx(UIHelpIcon, {
      className: "m-left-1",
      color: CANDY_APPLE,
      title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "campaigns.adwords.keywordLowSearchVolumeTooltip"
      })
    }),
    children: entity.name
  });
};

const EntityTypeStatusTagMap = {
  [EntityTypes.SEQUENCE]: CampaignStatusTagWrapper,
  [EntityTypes.CAMPAIGN]: CampaignStatusTagWrapper,
  [EntityTypes.ADGROUP]: AdGroupStatusTagWrapper,
  [EntityTypes.AD]: AdStatusTagWrapper,
  [EntityTypes.KEYWORD]: AdStatusTagWrapper
};
const EntityTypeSummaryMap = {
  [Networks.ADWORDS]: {},
  [Networks.FACEBOOK]: {
    [EntityTypes.ADGROUP]: FBAdSetSummary,
    [EntityTypes.AD]: FBAdSummary
  },
  [Networks.LINKEDIN]: {
    [EntityTypes.ADGROUP]: LIAdSetSummary,
    [EntityTypes.AD]: LIAdSummary
  }
};
const EntityTypeNameInfoMap = {
  [Networks.ADWORDS]: {
    [EntityTypes.AD]: AdWordsAdNameInfo,
    [EntityTypes.KEYWORD]: AdWordsKeywordNameInfo
  },
  [Networks.FACEBOOK]: {},
  [Networks.LINKEDIN]: {}
};

const NullComponent = () => null;

const CampaignReportingNameCell = ({
  parent,
  entity,
  entityType,
  adAccount,
  campaignEntity,
  timeRange,
  trackingStatus,
  hasControlButtons,
  onClickEntity,
  errorsByAdAccount,
  hasCampaignManagement,
  query,
  setQueryParam,
  sequenceStep
}) => {
  const StatusTag = EntityTypeStatusTagMap[entityType];
  const SummaryComponent = EntityTypeSummaryMap[adAccount.network][entityType] || NullComponent;
  const NameInfoComponent = EntityTypeNameInfoMap[adAccount.network][entityType] || NameInfo;

  const NameCellContent = /*#__PURE__*/_jsx(UIMediaObject, {
    align: entityType === EntityTypes.CAMPAIGN ? 'center' : 'start',
    itemLeft: entityType === EntityTypes.CAMPAIGN || entityType === EntityTypes.SEQUENCE ? /*#__PURE__*/_jsx(NetworkIcon, {
      network: entity.network
    }) : hasCampaignManagement && entity.imageUrl ? /*#__PURE__*/_jsx(UIImage, {
      src: entity.imageUrl,
      width: 75
    }) : null,
    children: /*#__PURE__*/_jsxs("div", {
      children: [EntityChildren[entityType] ? /*#__PURE__*/_jsx(UILink, {
        onClick: () => onClickEntity(entity),
        children: /*#__PURE__*/_jsx(NameInfoComponent, {
          entity: entity
        })
      }) : /*#__PURE__*/_jsx(NameInfoComponent, {
        entity: entity
      }), !!StatusTag && /*#__PURE__*/_jsxs(SingleLine, {
        className: "m-y-2",
        children: [/*#__PURE__*/_jsx("span", {
          className: "no-shrink",
          children: /*#__PURE__*/_jsx(StatusTag, {
            entity: entity,
            parentEntity: parent,
            campaignEntity: campaignEntity
          })
        }), trackingStatus && entityType !== EntityTypes.KEYWORD && /*#__PURE__*/_jsx(TrackingStatusTag, {
          adEntity: entityType === EntityTypes.AD ? entity : null,
          adGroupEntity: entityType === EntityTypes.AD ? parent : entityType === EntityTypes.ADGROUP ? entity : null,
          campaignEntity: campaignEntity || entity,
          adAccount: adAccount,
          trackingStatus: trackingStatus,
          timeRange: timeRange,
          entityType: entityType
        })]
      }), hasCampaignManagement && /*#__PURE__*/_jsx(SummaryComponent, {
        entity: entity,
        adAccount: adAccount
      })]
    })
  });

  return /*#__PURE__*/_jsx(UIPopover, {
    use: "shepherd",
    width: 350,
    open: query.get('enableShepherdPopover') === 'true' && entityType === EntityTypes.AD && entity.id === query.get('adIds').split(',')[0],
    showCloseButton: true,
    onOpenChange: () => {
      setQueryParam('enableShepherdPopover', 'false', false);
    },
    placement: "right",
    autoPlacement: false,
    content: {
      header: /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsxs(H4, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "campaigns.campaignDetailsTable.smartRulesShepherd.header"
          }), /*#__PURE__*/_jsx(UIBadge, {
            use: "new",
            className: "m-left-3",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "new"
            })
          })]
        })
      }),
      body: /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaigns.campaignDetailsTable.smartRulesShepherd.body"
        }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(KnowledgeBaseLink, {
          kbArticle: KBArticles.SMART_RULES,
          context: 'smart-rule-shepherd'
        })]
      }),
      footer: /*#__PURE__*/_jsx(UIButton, {
        use: "primary",
        href: `/website/${PortalIdParser.get()}/pages/site`,
        size: "small",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaigns.campaignDetailsTable.smartRulesShepherd.button"
        })
      })
    },
    children: /*#__PURE__*/_jsx(UITableHoverCell, {
      className: "campaign-name-cell",
      tooltip: /*#__PURE__*/_jsx("div", {
        className: "campaign-name-cell-inside-tooltip",
        children: NameCellContent
      }),
      hoverContent: hasControlButtons ? /*#__PURE__*/_jsx(CampaignReportingControlButtonsContainer, {
        parent: parent,
        entity: entity,
        entityType: entityType,
        adAccount: adAccount,
        campaignEntity: entityType === EntityTypes.CAMPAIGN || entityType === EntityTypes.SEQUENCE ? entity : campaignEntity,
        errorsByAdAccount: errorsByAdAccount,
        sequenceStep: sequenceStep
      }) : null,
      style: {
        height: '100%',
        minWidth: 500
      },
      children: NameCellContent
    })
  });
};

export default withQueryParams(CampaignReportingNameCell);